import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./MobileOtpPage.css"; // Ensure this matches your V3 CSS filename
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import callApi from "../../../../utility/apiCaller";
import { setUserClickData } from "../../../../utility/setUserClickData";
import { login } from "../../../../store/app/appReducer";
import OTPInput from "react-otp-input";
import moment from "moment";
import { setInternalLead } from "../../../../store/app/aaSlice";

const MobileOtpPage = ({
  isFormStarted,
  setIsFormStarted,
  currentStep,
  setCurrentStep,
  formData,
  setFormData
}) => {
  // --- UI FLOW STATE ---
  const [showContactForm, setShowContactForm] = useState(false);
  const [isOtpGenerated, setIsOtpGenerated] = useState(false);

  // --- CALCULATOR STATE ---
  const [goldWeight, setGoldWeight] = useState(40);
  const [goldPurity, setGoldPurity] = useState(22);

  // --- FORM STATE ---
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [pincode, setPincode] = useState("");
  const [isConsentChecked, setIsConsentChecked] = useState(true);
  const [otp, setOtp] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [leadId, setLeadId] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  // --- TRACKING & PARAMS ---
  const [params] = useSearchParams();
  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const affId = params.get("aff_id") || "";
  const dispatch = useDispatch();

  // --- TIMER STATE ---
  const RESEND_OTP_TIME = 30;
  const [resendCounter, setResendCounter] = useState(RESEND_OTP_TIME);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [otpTimerKey, setOtpTimerKey] = useState(0);

  // --- DYNAMIC CALCULATOR LOGIC ---
  const calculations = useMemo(() => {
    const baseRate24K = 7450;
    const weight = Number(goldWeight) || 0;
    const purityMultiplier = goldPurity / 24;

    const goldValue = Math.round(weight * baseRate24K * purityMultiplier);
    const approxLoan = Math.round(goldValue * 0.75);
    const monthlyInterest = Math.round((approxLoan * 0.14) / 12);

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN').format(val || 0);

    return {
      goldValue: formatCurrency(goldValue),
      approxLoanAmount: formatCurrency(approxLoan),
      monthlyInterest: formatCurrency(monthlyInterest),
      rawApproxLoan: approxLoan
    };
  }, [goldWeight, goldPurity]);

  // --- EFFECTS ---
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        setIpAddress(data?.ip);
      } catch (error) {
        console.log("Error fetching IP:", error);
      }
    };
    fetchIp();
  }, []);

  useEffect(() => {
    if (!isOtpGenerated) return;
    setResendCounter(RESEND_OTP_TIME);
    setCanResendOtp(false);
    const interval = setInterval(() => {
      setResendCounter((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResendOtp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isOtpGenerated, otpTimerKey]);

  const formatResendTime = (seconds) => {
    const clamped = Math.max(0, Number(seconds) || 0);
    const mm = String(Math.floor(clamped / 60)).padStart(2, "0");
    const ss = String(clamped % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const handleValidation = () => {
    if (!/^\d{10}$/.test(mobile) || mobile.length !== 10) return false;
    return true;
  };

  // --- STEP 1 VALIDATION ---
  const handleProceedToStep2 = () => {
    if (!calculations.rawApproxLoan || calculations.rawApproxLoan <= 0) {
      return toast.error("Please enter a valid gold weight to calculate your loan amount.");
    }
    setShowContactForm(true);
  };

  // --- API LOGIC ---
  const generateLead = async (token) => {
    try {
      const res = await callApi(
        "v1/lead/dmi-lead",
        "post",
        {
          lead: {
            contact_phone: mobile,
            contact_name: name,
            pincode: pincode,
            steps_completed: 1,
            alliance_id: "gold-loan",
            aff_id: affId || null,
            utm_source: utmSource || null,
            utm_medium: utmMedium || null,
            ip_address: ipAddress,
            kyc_consent: isConsentChecked,
            kyc_consent_datetime: new Date().toISOString(),
            amount_required: calculations.rawApproxLoan,
          },
        },
        "core",
        token,
      );
      if (!res || res.status !== "Success") return false;
      const finalLeadId = res?.data?.lead?._id;
      if (!finalLeadId) return false;
      dispatch(setInternalLead(finalLeadId));
      return finalLeadId;
    } catch (error) {
      toast.error("Something went wrong while generating lead.");
      return false;
    }
  };

  const fetchLeadData = async (token) => {
    try {
      if (leadId) return { hasLead: false, hasOffer: false };
      const startDate = moment().subtract(1, "months").startOf("day").toISOString();
      const endDate = moment().endOf("day").toISOString();

      const response = await callApi(
        "v1/lead/list",
        "post",
        { filters: { contact_phone: mobile, created_at: { $gte: startDate, $lte: endDate } } },
        "core",
      );

      const sortedLeads = (response?.data?.leadList || []).sort(
        (a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt),
      );
      const lead = sortedLeads[0];

      if (lead) {
        return { hasLead: true, hasOffer: false, leadId: lead._id };
      } else {
        const newLeadId = await generateLead(token);
        return { hasLead: false, hasOffer: false, leadId: newLeadId };
      }
    } catch (error) {
      return { hasLead: false, hasOffer: false, leadId: null };
    }
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) return toast.error("Please enter a valid name.");
    if (!handleValidation()) return toast.error("Please enter a valid 10-digit mobile number.");
    if (!/^\d{6}$/.test(pincode)) return toast.error("Please enter a valid 6-digit pincode.");
    if (!isConsentChecked) return toast.error("Please agree to the Terms and Conditions.");

    try {
      setIsLoading(true);
      const response = await callApi("v1/sms/send-otp", "post", { contact_phone: mobile, kyc_consent: isConsentChecked }, "messaging");

      if (response.status === "Success") {
        setUserClickData({ event_name: `otp-send-for-gold-loan-v2`, user_id: mobile, affiliate_id: affId, utm_medium: utmMedium });
        if (typeof setFormData === "function") {
          setFormData((prev) => ({ ...prev, name: name, fullName: name, mobile: mobile, pincode: pincode, amount_required: calculations.rawApproxLoan, stepDone: 3 }));
        }
        localStorage.setItem("user_name", name);
        localStorage.setItem("pincode", pincode);
        setIsOtpGenerated(true);
        if(typeof setIsFormStarted === "function") setIsFormStarted();
        setOtpTimerKey((prev) => prev + 1);
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while sending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    try {
      setIsLoading(true);
      const response = await callApi("v1/sms/send-otp", "post", { contact_phone: mobile, kyc_consent: isConsentChecked, kyc_consent_datetime: new Date().toISOString() }, "messaging");
      if (response.status === "Success") {
        setUserClickData({ event_name: `resend-otp-for-gold-loan-v2`, user_id: mobile, affiliate_id: affId, utm_medium: utmMedium });
        setOtpTimerKey((prev) => prev + 1);
      } else {
        toast.error("Failed to resend OTP.");
      }
    } catch (error) {
      toast.error("An error occurred while resending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (otp.length !== 4) return toast.error("Please enter a valid 4-digit OTP.");
    try {
      setIsLoading(true);
      const otpResponse = await callApi("v1/sms/validate-otp", "post", { contact_phone: mobile, otp }, "messaging");

      if (otpResponse.status === "Success") {
        setUserClickData({ event_name: `verify-otp-gold-loan-v2`, user_id: mobile, affiliate_id: affId, utm_source: utmSource, utm_medium: utmMedium });

        const freshToken = otpResponse.data.token;
        dispatch(login({ ...otpResponse.data.customer, token: freshToken }));
        localStorage.setItem("mobile", mobile);

        const leadResponse = await callApi(
          "v1/lead/lead-from-phone",
          "post",
          { phone: mobile, website_kyc_consent: isConsentChecked, website_kyc_consent_datetime: new Date().toISOString(), kyc_consent: isConsentChecked, kyc_consent_datetime: new Date().toISOString(), utm_source: utmSource, otp_page: "gold-loan-v2", ip_address: ipAddress, amount_required: calculations.rawApproxLoan },
          "core",
          freshToken,
        );

        const fetchedLeadId = leadResponse?.data?.lead?._id;
        if(typeof setCurrentStep === "function") setCurrentStep(2);

        if (fetchedLeadId) {
          try {
            fetch("https://campaigns.digitmoney.in/api/offercheck/requestjson.php?case=insert_digit_money_data", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ api_key: "fca6416db1074658d6b6823380d18f04", mobile: mobile, lead_id: fetchedLeadId }),
            }).catch(err => console.error(err));
          } catch (e) {
            console.error(e);
          }

          const lendersToPush = [
            { lender_id: "6ac73876ff51f5d7328d7ca1", lender_name: "Muthoot FinCorp" },
            { lender_id: "69a2c7ab1f6f4ddfd5577314", lender_name: "IIFL Finance" },
            { lender_id: "69ea0b864084a68bdadc40bd", lender_name: "Muthoot Finance" },
            { lender_id: "6abb59d6a3ee46254d954755", lender_name: "Oro Money" },
            { lender_id: "6aa03dd160f34db15292641e", lender_name: "Rupeek GL" },
            { lender_id: "6ac37efdf072975a2b2502a2", lender_name: "Capri Global" },
          ];

          Promise.allSettled(
            lendersToPush.map((lender) =>
              callApi("v1/lead/bulk-lead-push-by-leadId", "post", { leads: [{ lead_id: fetchedLeadId }], lender_name: lender.lender_name, lender_id: lender.lender_id }, "core", freshToken)
            )
          ).catch(err => console.error("Bulk push error:", err));
        }
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (otpError) {
      console.error(otpError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div id="v3-strict-scope" className="new-hero-desktop-wrapper">
      <div className="new-hero-grid">

        {/* =========================================
            LEFT CONTENT AREA
            ========================================= */}
        <div className="hero-left-content">
          <p className="gold-subtitle">GOLD LOAN CALCULATOR</p>
          <h1 className="hero-main-title">
            {/* Dynamic title swap based on showContactForm */}
            {!showContactForm ? (
              <>Calculate Your Gold<br />Loan Eligibility</>
            ) : (
              <>Gold Loan made simple.</>
            )}
          </h1>
          <p className="hero-description">
            {/* Shows ONLY on Desktop */}
            <span className="desktop-desc">
              Discover your gold's value and explore loan options tailored to your needs.
            </span>

            {/* Shows ONLY on Mobile */}
            <span className="mobile-desc">
              Check how much you may be eligible for.
            </span>
          </p>

          <div className="trust-badges-row">
            <img src="/assets/img/Group 516.svg" alt="Google Rating & Trust" className="trust-badges-img-desktop" />
          </div>

          <div className="disbursed-badge-card">
            <img src="/assets/img/Group 565.svg" alt="Gold" className="gold-bars-large-img" />
          </div>

          <div className="hero-features">
            <span><img src="/assets/img/goldenChecked.svg" alt="check" /> No hidden calculations</span>
            <span><img src="/assets/img/goldenChecked.svg" alt="check" /> Takes less than a minute</span>
          </div>
        </div>

        {/* =========================================
            RIGHT DYNAMIC CARD AREA
            ========================================= */}
        <div className="hero-right-calculator">

          {!showContactForm ? (

            // 🟢 STEP 1: CALCULATOR CARD
            <div className="ty-calc-container">
              <h2 className="ty-calc-title">Tell us about your gold</h2>
              <div className="ty-calc-grid">
                {/* Gold Weight Input */}
                <div className="ty-calc-col">
                  <div className="ty-labels-row">
                    <div className="ty-main-label">
                      Gold Weight<br />(grams)
                    </div>
                    <div className="ty-helper-text">
                      Enter grams of gold in<br />hand
                    </div>
                  </div>
                  <div className="ty-input-box">
                    <input
                      type="number"
                      value={goldWeight}
                      onChange={(e) => setGoldWeight(e.target.value)}
                      placeholder="40"
                    />
                    <span className="ty-input-unit">grams</span>
                  </div>
                </div>

                {/* Carats Selection */}
                <div className="ty-calc-col">
                  <div className="ty-labels-row">
                    <div className="ty-main-label">Carats (Purity)</div>
                  </div>
                  <div className="ty-carat-buttons-container">
                    {[18, 20, 22, 24].map((carat) => (
                      <button
                        key={carat}
                        type="button"
                        className={`ty-carat-btn ${goldPurity === carat ? 'active' : ''}`}
                        onClick={() => setGoldPurity(carat)}
                      >
                        {carat}K
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Middle Results Gradient Card */}
              <div className="ty-results-card">
                <div className="ty-results-top">
                  <div>
                    <p className="ty-results-label">APPROX. LOAN AMOUNT</p>
                    <p className="ty-results-subtext">Up to 75% of estimated gold value</p>
                  </div>
                  <h2 className="ty-huge-amount">₹{calculations.approxLoanAmount}</h2>
                </div>

                <div className="ty-divider"></div>

                <div className="ty-results-metrics">
                  <div className="ty-metric-col">
                    <p className="ty-results-label">GOLD VALUE</p>
                    <h3>₹{calculations.goldValue}</h3>
                  </div>
                  <div className="ty-metric-col">
                    <p className="ty-results-label">TENURE</p>
                    <h3>3–12 months</h3>
                  </div>
                  <div className="ty-metric-col">
                    <p className="ty-results-label">MONTHLY INTEREST PAYABLE</p>
                    <h3>₹{calculations.monthlyInterest} <span className="ty-small-unit">/ month</span></h3>
                  </div>
                </div>

                <div className="ty-divider"></div>

                <div className="ty-results-footer">
                  <span className="ty-assumptions-badge">SAMPLE ASSUMPTIONS</span>
                  <span className="ty-assumptions-text">
                    24K gold rate ₹7,450/g · 75% LTV · 14% p.a. interest
                  </span>
                </div>
              </div>

              {/* Bottom CTA Button */}
              <button className="ty-cta-btn" onClick={handleProceedToStep2}>
                <span>Get Lender Options Near You</span>
                <div className="ty-cta-arrow">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>
              </button>

              <p className="ty-disclaimer-text">
                These are indicative estimates. Actual gold valuation, loan eligibility, interest rates, and tenure depend on the lender's policies.<br />
                Jewellery valuation may exclude stones and making charges.
              </p>
            </div>

          ) : !isOtpGenerated ? (

            // 🟠 STEP 2: PERSONAL DETAILS CARD
            <div className="calculator-card form-step-card">
              <button className="back-btn-text" onClick={() => setShowContactForm(false)}>
                ← Back to calculator
              </button>

              <div className="mini-progress-bar">
                <div className="m-step completed">1</div>
                <div className="m-line completed-line"></div>
                <div className="m-step completed">2</div>
                <div className="m-line completed-line"></div>
                <div className="m-step active">3</div>
              </div>

              <div className="form-titles">
                <p className="tiny-subtitle-grey">Tell us about yourself</p>
                <h2 className="step-title">Check your gold loan options</h2>
              </div>

              <form onSubmit={handleHeroSubmit} className="step-form-body">
                {/* Full Name Floating Label Box */}
                <div className="floating-input-box">
                  <label className="floating-label">Full Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Priya Sharma"
                    value={name}
                    onChange={(e) => setName(e.target.value.replace(/[^A-Za-z\s]/g, ""))}
                    required
                  />
                </div>

                {/* Mobile Number Box */}
                <div className="custom-input-box mobile-box">
                  <img src="/assets/img/Group 372.svg" alt="We don't spam" className="floating-badge-svg" />
                  <div className="mobile-flex-wrapper">
                    <div className="flag-prefix">🇮🇳 <span>▾</span></div>
                    <div className="input-content-col">
                      <label className="inner-field-label">Mobile Number</label>
                      <input type="tel" maxLength="10" placeholder="10-digit mobile number" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} required />
                    </div>
                  </div>
                </div>

                {/* Pin Code Floating Label Box */}
                <div className="floating-input-box">
                  <label className="floating-label">Pin code</label>
                  <input
                    type="text"
                    maxLength="6"
                    placeholder="6-digit postal code"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                    required
                  />
                </div>

                <label className="terms-checkbox">
                  <input type="checkbox" checked={isConsentChecked} onChange={() => setIsConsentChecked(!isConsentChecked)} required />
                  <span>
                    By submitting this form, you have read and agree to the
                    <a href="#"> Credit Report Terms of Use</a>,
                    <a href="/terms"> Terms of Use</a> &
                    <a href="/privacy"> Privacy Policy</a> of DigitMoney & its
                    <a href="/lenders"> Lending Partners</a>
                  </span>
                </label>

                <button type="submit" className="get-options-btn" disabled={isLoading}>
                  {isLoading ? "Processing..." : "Check my gold loan options"}
                  <span className="arrow-circle">➔</span>
                </button>
                <p className="secure-text-bottom">Your details are secure</p>
              </form>
            </div>
          ) : (

            // 🟡 STEP 3: OTP CARD
            <div className="calculator-card form-step-card otp-mobile-card">

              <button className="back-btn-text otp-hide-mobile" onClick={() => setIsOtpGenerated(false)}>
                ← Back to Personal Details
              </button>

              <div className="mini-progress-bar otp-hide-mobile">
                <div className="m-step completed">1</div>
                <div className="m-line completed-line"></div>
                <div className="m-step completed">2</div>
                <div className="m-line completed-line"></div>
                <div className="m-step active">3</div>
              </div>

              <div className="otp-center-content">
                <div className="otp-titles-wrapper">
                  <h2 className="step-title">Verify Your Mobile Number</h2>
                  <p className="secure-text-sub">Your data is encrypted & safe</p>
                </div>

                <p className="otp-sent-text">
                  OTP sent to <span className="golden-text">+91 XXXXXX{mobile.slice(-4) || "XXXX"}</span>
                </p>

                <div className="otp-input-container">
                  <OTPInput
                    value={otp}
                    onChange={(val) => setOtp(val)}
                    numInputs={4}
                    isInputNum={true}
                    shouldAutoFocus={true}
                    inputStyle="golden-box-input"
                    containerStyle="golden-box-container"
                    renderInput={(props) => (
                      <input 
                        {...props} 
                        inputMode="numeric" 
                        pattern="[0-9]*" 
                        autoComplete="one-time-code" 
                      />
                    )}
                  />
                </div>

                <div className="otp-bottom-section">
                  <div className="otp-resend-wrapper">
                    Didn't receive the OTP?{" "}
                    {canResendOtp ? (
                      <button type="button" className="otp-resend-btn" onClick={handleResendOtp} disabled={isLoading}>
                        Resend OTP
                      </button>
                    ) : (
                      <span className="otp-resend-timer">Resend in {formatResendTime(resendCounter)} Sec</span>
                    )}
                  </div>

                  <button type="button" className="get-options-btn verify-btn" onClick={handleOtpSubmit} disabled={otp.length !== 4 || isLoading}>
                    {isLoading ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>

              </div>
            </div>

          )}

        </div>
      </div>
    </div>
  );
};

export default MobileOtpPage;